import { clamp01, pct } from './geometry'
import './viz.css'

export interface HistBin {
  label: string
  count: number
  from: number
  to: number
}

interface HistogramProps {
  bins: HistBin[]
  domain: [number, number]
  threshold?: number
  thresholdLabel?: string
  ariaLabel: string
}

// Bin edges are usually computed (lo + i × step), so a bin whose edge is
// nominally the threshold can land a few ulps below it; count that as at it.
const EDGE_TOLERANCE = 1e-9

const safeCount = (count: number) => (Number.isFinite(count) && count > 0 ? count : 0)

/**
 * Count bars (height relative to the tallest) over a fixed value domain, with
 * an optional dashed threshold line. Bins that start at or above the threshold
 * are marked `--over`. The line is placed on the bars area by the domain
 * mapping, so it sits on the bin boundary when the bins tile the domain.
 */
export function Histogram({ bins, domain, threshold, thresholdLabel, ariaLabel }: HistogramProps) {
  const max = Math.max(0, ...bins.map((b) => safeCount(b.count)))
  // The threshold, or null when it is absent or not a finite number.
  const line = typeof threshold === 'number' && Number.isFinite(threshold) ? threshold : null
  const [lo, hi] = domain
  const frac = line !== null && hi > lo ? clamp01((line - lo) / (hi - lo)) : 0
  const labelled = line !== null && Boolean(thresholdLabel)

  return (
    <div className={`viz-hist${labelled ? ' viz-hist--labelled' : ''}`} role="img" aria-label={ariaLabel}>
      <div className="viz-hist-bars">
        {bins.map((bin, i) => {
          const count = safeCount(bin.count)
          const over = line !== null && bin.from >= line - EDGE_TOLERANCE
          return (
            <div
              className={`viz-hist-bar${over ? ' viz-hist-bar--over' : ''}`}
              key={`${bin.label}-${i}`}
              style={{ height: pct(max > 0 ? (count / max) * 100 : 0) }}
            />
          )
        })}
        {line !== null && (
          <div
            className={`viz-hist-threshold${frac > 0.5 ? ' viz-hist-threshold--end' : ''}`}
            style={{ left: pct(frac * 100) }}
          >
            {thresholdLabel ? <span className="viz-hist-threshold-label">{thresholdLabel}</span> : null}
          </div>
        )}
      </div>
      <div className="viz-hist-axis">
        {bins.length > 0 && <span>{bins[0].label}</span>}
        {bins.length > 1 && <span>{bins[bins.length - 1].label}</span>}
      </div>
    </div>
  )
}
